import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BASIS_LABELS,
  PURPOSE_LABELS,
  STAGES,
  isFiscal,
  type Purpose,
} from '@ave/shared';
import { api, type EngagementSummary } from '../lib/api';
import { longDate, relativeTime } from '../lib/format';
import { Badge, Card, EmptyState, ErrorNotice, Spinner, type Tone } from '../components/ui';
import { Icon } from '../components/icons';
import { NewEngagement } from '../components/NewEngagement';
import { useSession } from '../App';

const PURPOSE_TONE: Partial<Record<Purpose, Tone>> = {
  iht_estate: 'violet',
  cgt: 'indigo',
  emi_csop: 'sky',
  ers_other: 'sky',
  commercial_sale: 'emerald',
  dispute: 'rose',
  financial_reporting: 'teal',
  matrimonial: 'amber',
};

export function EngagementsPage() {
  const me = useSession();
  const [engagements, setEngagements] = useState<EngagementSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api
      .listEngagements()
      .then((r) => setEngagements(r.engagements))
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <ErrorNotice title="Could not load engagements" message={error} />;
  if (!engagements) return <Spinner label="Loading engagements" />;

  const active = engagements.filter((e) => e.status === 'active');
  const released = engagements.filter((e) => e.status !== 'active');
  const needingAttention = engagements.filter(
    (e) => e.openExceptions > 0 || e.currentStage === 9,
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Engagements</h1>
          <p className="mt-1 text-sm text-slate-500">
            {me.tenant.name} · every valuation runs the same eleven gated stages, whatever its
            purpose.
          </p>
        </div>
        <button
          className="btn-primary"
          disabled={me.user.licenceReadOnly || me.user.role === 'viewer'}
          onClick={() => setCreating(true)}
        >
          <Icon name="sparkles" className="h-4 w-4" />
          New engagement
        </button>
      </header>

      {creating ? <NewEngagement onClose={() => setCreating(false)} /> : null}

      {needingAttention.length > 0 ? (
        <Card className="overflow-hidden border-amber-200">
          <div className="flex items-start gap-3 bg-amber-50/70 px-6 py-4">
            <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {needingAttention.length} engagement{needingAttention.length === 1 ? '' : 's'} waiting
                on you
              </p>
              <ul className="mt-2 space-y-1">
                {needingAttention.map((e) => (
                  <li key={e.id} className="text-sm text-amber-800">
                    <Link to={`/engagements/${e.id}`} className="font-medium underline-offset-2 hover:underline">
                      {e.ref}
                    </Link>{' '}
                    —{' '}
                    {e.openExceptions > 0
                      ? `${e.openExceptions} assumption${e.openExceptions === 1 ? '' : 's'} outside corridor, blocking the stage gate`
                      : 'at the review gate, awaiting sign-off'}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      ) : null}

      {engagements.length === 0 ? (
        <Card>
          <EmptyState
            title="No engagements yet"
            detail="Create one to capture the purpose, basis and valuation date — everything downstream is gated on those three answers."
          />
        </Card>
      ) : (
        <section className="space-y-4">
          <h2 className="label">In progress ({active.length})</h2>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {active.map((e) => (
              <EngagementCard key={e.id} engagement={e} />
            ))}
          </div>
        </section>
      )}

      {released.length > 0 ? (
        <section className="space-y-4">
          <h2 className="label">Released &amp; archived ({released.length})</h2>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {released.map((e) => (
              <EngagementCard key={e.id} engagement={e} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function EngagementCard({ engagement: e }: { engagement: EngagementSummary }) {
  const stage = STAGES[e.currentStage];
  const isReleased = e.status !== 'active';

  return (
    <Link
      to={`/engagements/${e.id}`}
      className="card card-hover group relative overflow-hidden p-6 hover:border-teal-300"
    >
      <div className="absolute left-0 top-0 h-1 w-full origin-left scale-x-0 bg-gradient-to-r from-teal-500 to-teal-600 transition-transform duration-300 group-hover:scale-x-100" />

      <div className="flex items-start justify-between gap-3">
        <span className="font-mono text-xs font-medium text-slate-400">{e.ref}</span>
        {isReleased ? (
          <Badge tone="emerald">
            <Icon name="check" className="h-3 w-3" strokeWidth={2.5} />
            Released
          </Badge>
        ) : e.openExceptions > 0 ? (
          <Badge tone="amber">
            {e.openExceptions} exception{e.openExceptions === 1 ? '' : 's'}
          </Badge>
        ) : (
          <Badge tone="slate">Stage {e.currentStage}</Badge>
        )}
      </div>

      <h3 className="mt-3 line-clamp-2 text-[15px] font-semibold leading-snug text-slate-900">
        {e.name}
      </h3>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Badge tone={PURPOSE_TONE[e.purpose] ?? 'slate'}>{PURPOSE_LABELS[e.purpose]}</Badge>
        {isFiscal(e.purpose) ? <Badge tone="slate">Fiscal</Badge> : null}
      </div>

      <dl className="mt-4 space-y-1.5 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-400">Valuation date</dt>
          <dd className="tnum text-slate-700">{longDate(e.valuationDate)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-400">Basis</dt>
          <dd className="truncate text-right text-slate-700" title={BASIS_LABELS[e.basisOfValue]}>
            {BASIS_LABELS[e.basisOfValue].split('—')[1]?.trim() ?? BASIS_LABELS[e.basisOfValue]}
          </dd>
        </div>
      </dl>

      <div className="mt-5">
        <StageStrip currentStage={e.currentStage} released={isReleased} />
        <p className="mt-2 text-xs text-slate-500">
          {isReleased ? 'Archived defence file written' : `Stage ${e.currentStage} · ${stage?.name}`}
        </p>
      </div>

      <p className="mt-4 text-xs text-slate-400">Updated {relativeTime(e.updatedAt)}</p>
    </Link>
  );
}

/** Eleven segments, one per stage — the shape of the workflow at a glance. */
function StageStrip({ currentStage, released }: { currentStage: number; released: boolean }) {
  return (
    <div className="flex gap-[3px]">
      {STAGES.map((s) => {
        const done = released || s.stage < currentStage;
        const current = !released && s.stage === currentStage;
        return (
          <span
            key={s.stage}
            title={`${s.stage}. ${s.name}`}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              done ? 'bg-teal-500' : current ? 'bg-teal-300' : 'bg-slate-200'
            }`}
          />
        );
      })}
    </div>
  );
}
